import { DashboardService } from './dashboard.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { rotate } from '../animations/rotate.animation';
import { openClose } from '../animations/open-close.animation';
import { TranslateService } from '@ngx-translate/core';

import {
  NameFilter,
  ModalService,
  CommunicationManagerService,
  CompanyCostItemService,
  RecurringCostService,
  SessionManagerService,
  LoadingService,
  NavigationItem,
  CompanyCostItem,
  RecurringCost,
  FileArrays,
  LocalStorageService,
  ProjectManagerService,
  StripeService,
  DataSyncService,
  ProjectStatusGuardService,
} from '@ops/shared-types';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  animations: [rotate, openClose],
})
export class DashboardComponent implements OnInit, OnDestroy {
  @Output() loadEstimate: EventEmitter<string> = new EventEmitter<string>();
  @Output() loadCost: EventEmitter<string> = new EventEmitter<string>();
  @Output() loadRegistry: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() isListMode: boolean = false;
  @ViewChild('menu') menu: ElementRef<HTMLDivElement>;

  listMode: boolean = false;
  isUserLoggedIn: boolean = false;
  selectedCompanyId: string;

  isLoading: boolean = true;

  show_M4: boolean;
  show_restriction: boolean;
  isMenuOpen = false;

  selectedCompany$: Observable<NameFilter>;
  selectedCompanyCompanyService$: Observable<NameFilter>;
  companies$: Observable<NameFilter[]>;
  monthlyOverviewData$: any;
  quarterlyBalance$: any;

  dataRefetchBehaviorSubject$: Observable<any>;

  selectedCompanyIdStats: string;

  subscriptions: Array<Subscription> = [];

  loadingTimeout: any;

  constructor(
    private modalService: ModalService,
    private cm: CommunicationManagerService,
    private dashBoardService: DashboardService,
    private router: Router,
    private companyCostItemService: CompanyCostItemService,
    private recurringService: RecurringCostService,
    private session: SessionManagerService,
    private loader: LoadingService,
    private ls: LocalStorageService,
    private pm: ProjectManagerService,
    private dataSyncService: DataSyncService,
    private translateService: TranslateService,
    private projectStatusGuard: ProjectStatusGuardService
  ) {
    const lsCompany = this.ls.getCurrentCompany();
    if (lsCompany) {
      this.dashBoardService.selectCompany(lsCompany);
    }
    this.selectedCompany$ = this.dashBoardService.selectedCompany$;
    this.dataRefetchBehaviorSubject$ =
      this.dataSyncService.getRefetchVersionListener();

    this.companies$ = combineLatest([
      this.dashBoardService.getCompanies(),
      this.selectedCompany$.pipe(startWith({ _id: null })),
    ]).pipe(
      tap({
        next: ([companies, selectedCompany]) => {
          // console.log('selectedCompany?._id', selectedCompany?._id);

          if (!selectedCompany?._id) {
            const lsCompany = this.ls.getCurrentCompany();
            if (lsCompany) {
              this.dashBoardService.selectCompany(lsCompany);
              this.selectedCompanyIdStats = companies?.[0]?._id;
            } else {
              this.dashBoardService.selectCompany(companies?.[0]);
              this.selectedCompanyIdStats = companies?.[0]?._id;
            }
          } else {
            this.selectedCompanyIdStats = selectedCompany?._id;
          }

          this.show_M4 = ['ADMIN', 'STAFF'].includes(
            this.session
              .getUser()
              .companies.find(
                (c) => c.company === selectedCompany?._id
                // ||
                //   c.company === companies?.[0]?._id
              )
              ?.role?.toUpperCase()
          );
        },
        error: (err) => {
          // console.log('AAA', err);
        },
      }),
      map(([companies]) => companies)
    );
    this.monthlyOverviewData$ = this.selectedCompany$.pipe(
      switchMap((selectedCompany) => {
        this.selectedCompanyId = selectedCompany?._id;

        return this.dashBoardService.getMonthlyOverviewData(
          selectedCompany?._id
        );
      })
    );
    this.quarterlyBalance$ = this.selectedCompany$.pipe(
      switchMap((selectedCompany) => {
        return this.dashBoardService.getQuarterlyBalance(selectedCompany?._id);
      })
    );
  }

  get selectedCompanyName(): string {
    return this.ls.getCurrentCompany()?.name ?? '';
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const clickedInsideDropdown = this.menu?.nativeElement?.contains(
      event.target as Node
    );
    if (!clickedInsideDropdown) {
      this.isMenuOpen = false;
    }
  }

  setLoadingTimeout(timeoutFor: number) {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    this.loadingTimeout = setTimeout(() => {
      this.isLoading = false;
      
    this.projectStatusGuard.setCurrentProject(null);

    }, timeoutFor);
  }

  get isLoadingMixed(): boolean {
    if (!this.isUserLoggedIn) return true;
    if (!this.selectedCompanyId) return true;

    return this.isLoading;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.listMode = false;
    this.listMode = this.isListMode;
    this.isUserLoggedIn = this.cm.isUserLoggedin();

    //TODO: this is a workaround, refactor
    const pSub = this.pm
      .getProjectSubscriptionMissingObservable()
      .subscribe((resp) => {
        // console.log('RESPPPP', resp);
        if (this.show_restriction !== resp) {
          this.show_restriction = resp;
        }
        // this.isLoading = false;
        // const timeoutFor = resp ? 100 : 300;
        this.setLoadingTimeout(400);
      });

    this.subscriptions.push(pSub);

    const dSub = this.dataRefetchBehaviorSubject$
      .pipe(take(1))
      .subscribe((dd) => {
        if (!!dd) {
          // console.log('REFETCH VERSION LISTENER', dd);
          this.dataSyncService.refetchVersionListener$.next(0);
          this.ls.clearEssential();
        }
      });

    this.subscriptions.push(dSub);

    let comp: NameFilter;
    const sub = this.selectedCompany$.pipe(take(1)).subscribe((c: any) => {
      comp = c;
      this.loader.stopLoadingSpinner();
    });
    this.subscriptions.push(sub);
    const subCompany = this.companies$.pipe(take(1)).subscribe((cs: any) => {
      comp = cs.find(
        (c: any) => c.name?.toUpperCase() == comp.name?.toUpperCase()
      );

      if (comp) {
        this.loader.stopLoadingSpinner();
        this.selectCompany(comp, true);
        this.loader.stopLoadingSpinner();
      }
    });
    this.subscriptions.push(subCompany);
  }

  ngOnDestroy(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    this.isLoading = true;
    this.show_restriction = undefined;
    this.show_M4 = false;

    this.subscriptions.forEach((s) => s?.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isListMode']) {
      this.listMode = this.isListMode;
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  isMobile(): boolean {
    return window.innerWidth < 768;
  }

  selectCompany(company: NameFilter, cold?: boolean) {
    // if(company._id === this.session.getCurrentCompany()) {
    //   this.isLoading = false;
    //   return;
    // }

    // this.isLoading = true;

    // console.log("SELECT COMPANY");
    if (!cold) {
      // if(this.loadingTimeout) {
      //   clearTimeout(this.loadingTimeout);
      // }
      if(this.isMobile() && this.isMenuOpen) {
        this.isMenuOpen = false;
      }
      this.isLoading = true;
      this.show_M4 = false;
      this.show_restriction = undefined;
      // this.isMenuOpen = false;
      // this.listMode = false;
    }
    this.dashBoardService.selectCompany(company);
    // ✅ FIXED: Removed redundant session service call - LocalStorageService is the single source of truth
    // this.setLoadingTimeout(500);
  }

  onAddCompanyCost() {
    this.openModal('add-cost-dashboard');
  }

  onActionClicked(action: NavigationItem) {
    if (action.route === 'add') {
      this.openModal('add-cost-dashboard');
      //console.log('open ', action);
      return;
    }
    this.router.navigate([action.route]);
  }

  onUserLogin(successful: boolean) {
    this.isUserLoggedIn = successful;
  }
  //TODO: should be handled at event generating click/element using router.navigate.
  onLoadEstimate(guid: string) {
    this.loadEstimate.emit(guid);
  }

  //TODO: should be handled at event generating click/element using roter.navigate.
  onLoadCost(guid: string) {
    this.loadCost.emit(guid);
  }

  //TODO: should be handled at event generating click/element using roter.navigate.
  onLoadRegistry(flag: boolean) {
    this.loadRegistry.emit(flag);
  }

  openModal(id: string) {
    this.modalService.open(id);
  }
  closeModal(id: string) {
    this.modalService.close(id);
  }

  onAddItem(data: { item: CompanyCostItem; files: FileArrays }) {
    this.loader.setForceLoading(true);
    data.item.company = this.selectedCompanyId;
    let companyItem: CompanyCostItem;
    const sub = this.companyCostItemService
      .createCompanyCostItem(data.item)
      .pipe(
        switchMap((resultItem: CompanyCostItem) => {
          companyItem = resultItem;
          return this.companyCostItemService.attachFiles(
            resultItem,
            'invoice',
            data.files.invoiceFiles
          );
        }),
        switchMap(() => {
          return this.companyCostItemService.attachFiles(
            companyItem,
            'payment',
            data.files.paymentFiles
          );
        })
      )
      .subscribe({
        next: (value: any) => {
          this.loader.setForceLoading(false);
          window.alert(
            this.translateService.instant('windowAlert.dashboard.costItemAddedWithFiles', { 
              fileCount: data.files.invoiceFiles.length + data.files.paymentFiles.length 
            })
          );
        },
        error: (error: any) => {
          this.loader.setForceLoading(false);
          window.alert(this.translateService.instant('windowAlert.cashflow.costItemCreatedWithError', { error: error.error.message }));
        },
      });
    this.subscriptions.push(sub);
  }

  onAddRecurringCost(item: RecurringCost) {
    this.loader.setForceLoading(true);
    item.company = this.selectedCompanyId;
    const sub = this.recurringService
      .createRecurringCost(item)
      .subscribe((recurringId) => {
        this.loader.setForceLoading(false);
        window.alert(this.translateService.instant('windowAlert.cashflow.recurringCostsCreated'));
      });
    this.subscriptions.push(sub);
  }

  onCancelAddItem() {}
}
